import { useCallback, useEffect, useRef, useState } from 'react';
import type { Participant, SyncState } from '../types.ts';

/**
 * Multi-user playback sync.
 *
 * The hard part is not sending play/pause — it is that every browser has a different clock
 * and a different latency to the server. So:
 *   1. ping/pong measures the offset between this browser's clock and the server's
 *   2. the controller stamps its state with the server clock
 *   3. followers project that state forward to *now* before comparing it to their own video
 *
 * Without step 1 and 3 the room drifts apart by exactly the network latency, which at 25 fps
 * is a visible frame or two.
 */

export interface DrawEvent {
  from: string;
  name: string;
  draw: unknown;
}

export interface SyncClient {
  connected: boolean;
  clientId: string | null;
  controllerId: string | null;
  isController: boolean;
  /** A token holder has taken the room over: only the controller may drive or draw. */
  locked: boolean;
  participants: Participant[];
  state: SyncState | null;
  /** Estimated serverTime - clientTime, in ms. */
  clockOffset: number;
  /** Round-trip time in ms, useful as a health readout. */
  rtt: number;
  serverNow: () => number;
  sendState: (patch: Partial<SyncState>) => void;
  takeControl: () => void;
  releaseControl: () => void;
  setReady: (ready: boolean) => void;
  sendDraw: (draw: unknown) => void;
  onDraw: (handler: (event: DrawEvent) => void) => () => void;
  onVersionChanged: (handler: (versionId: string, kind: string, payload: unknown) => void) => () => void;
}

export function useSyncClient(sessionId: string | null, displayName: string): SyncClient {
  const socketRef = useRef<WebSocket | null>(null);
  const [connected, setConnected] = useState(false);
  const [clientId, setClientId] = useState<string | null>(null);
  const [controllerId, setControllerId] = useState<string | null>(null);
  const [locked, setLocked] = useState(false);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [state, setState] = useState<SyncState | null>(null);
  const [clockOffset, setClockOffset] = useState(0);
  const [rtt, setRtt] = useState(0);

  const drawHandlers = useRef(new Set<(event: DrawEvent) => void>());
  const versionHandlers = useRef(new Set<(versionId: string, kind: string, payload: unknown) => void>());

  // Keep the best (lowest-RTT) sample: the shortest round trip has the least queueing noise.
  const bestRtt = useRef(Number.POSITIVE_INFINITY);

  useEffect(() => {
    if (!sessionId) return;

    bestRtt.current = Number.POSITIVE_INFINITY;
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const url = `${proto}://${location.host}/ws/session/${sessionId}?name=${encodeURIComponent(displayName)}`;
    const socket = new WebSocket(url);
    socketRef.current = socket;

    let pingTimer = 0;

    socket.onopen = () => {
      setConnected(true);
      const ping = (): void => {
        if (socket.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ t: 'ping', clientTime: Date.now() }));
        }
      };
      ping();
      pingTimer = window.setInterval(ping, 5000);
    };

    socket.onmessage = (event: MessageEvent<string>) => {
      const msg = JSON.parse(event.data) as Record<string, unknown>;
      switch (msg['t']) {
        case 'welcome':
          setClientId(msg['clientId'] as string);
          setControllerId(msg['controllerId'] as string | null);
          setLocked(Boolean(msg['locked']));
          setState(msg['state'] as SyncState);
          setParticipants(msg['participants'] as Participant[]);
          break;

        case 'participants':
          setParticipants(msg['participants'] as Participant[]);
          setControllerId(msg['controllerId'] as string | null);
          setLocked(Boolean(msg['locked']));
          break;

        case 'state':
          setState(msg['state'] as SyncState);
          break;

        case 'pong': {
          const sent = msg['clientTime'] as number;
          const serverTime = msg['serverTime'] as number;
          const now = Date.now();
          const roundTrip = now - sent;
          if (roundTrip <= bestRtt.current) {
            bestRtt.current = roundTrip;
            setRtt(roundTrip);
            // Assume the response took half the round trip to reach us.
            setClockOffset(serverTime + roundTrip / 2 - now);
          }
          break;
        }

        case 'draw':
          for (const handler of drawHandlers.current) {
            handler({ from: msg['from'] as string, name: msg['name'] as string, draw: msg['draw'] });
          }
          break;

        case 'versionChanged':
          for (const handler of versionHandlers.current) {
            handler(msg['versionId'] as string, msg['kind'] as string, msg['payload']);
          }
          break;

        default:
          break;
      }
    };

    socket.onclose = () => {
      setConnected(false);
      setClientId(null);
      setLocked(false);
    };

    return () => {
      window.clearInterval(pingTimer);
      socket.close();
      socketRef.current = null;
    };
  }, [sessionId, displayName]);

  const send = useCallback((payload: unknown) => {
    const socket = socketRef.current;
    if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(payload));
  }, []);

  const serverNow = useCallback(() => Date.now() + clockOffset, [clockOffset]);

  const sendState = useCallback(
    (patch: Partial<SyncState>) => {
      setState((prev) => (prev ? { ...prev, ...patch } : prev));
      send({ t: 'state', state: patch });
    },
    [send],
  );

  return {
    connected,
    clientId,
    controllerId,
    isController: clientId !== null && clientId === controllerId,
    locked,
    participants,
    state,
    clockOffset,
    rtt,
    serverNow,
    sendState,
    takeControl: useCallback(() => send({ t: 'takeControl' }), [send]),
    releaseControl: useCallback(() => send({ t: 'releaseControl' }), [send]),
    setReady: useCallback((ready: boolean) => send({ t: 'ready', ready }), [send]),
    sendDraw: useCallback((draw: unknown) => send({ t: 'draw', draw }), [send]),
    onDraw: useCallback((handler: (event: DrawEvent) => void) => {
      drawHandlers.current.add(handler);
      return () => drawHandlers.current.delete(handler);
    }, []),
    onVersionChanged: useCallback(
      (handler: (versionId: string, kind: string, payload: unknown) => void) => {
        versionHandlers.current.add(handler);
        return () => versionHandlers.current.delete(handler);
      },
      [],
    ),
  };
}

/**
 * Applies incoming sync state to a follower's video element.
 *
 * Returns the current drift in seconds so the UI can show whether the room is actually
 * together — a sync tool that silently drifts is worse than no sync at all.
 */
export function useFollowSync(
  videoRef: React.RefObject<HTMLVideoElement | null>,
  sync: SyncClient,
  active: boolean,
): number {
  const [drift, setDrift] = useState(0);

  // The sync client is a fresh object on every render. Reading it through a ref keeps the
  // correction loop from being torn down and restarted continuously.
  const syncRef = useRef(sync);
  syncRef.current = sync;

  // Publishing the drift on every animation frame would re-render the whole review page at
  // 60 Hz. It is a diagnostic readout, so a few updates per second is plenty.
  const lastPublished = useRef(0);

  useEffect(() => {
    if (!active) return;

    // Beyond this, catching up by rate alone would take longer than the seek itself.
    const HARD_SEEK = 0.5;
    // Below this we are inside a frame at 25 fps; leave it alone rather than hunting.
    const DEAD_ZONE = 0.08;

    let raf = 0;
    const tick = (): void => {
      const video = videoRef.current;
      const client = syncRef.current;
      const state = client.state;
      if (!video || !state) {
        raf = requestAnimationFrame(tick);
        return;
      }

      // Project the controller's sampled position forward to right now.
      const elapsed = state.playing ? (client.serverNow() - state.atServerTime) / 1000 : 0;
      const target = state.mediaTime + elapsed * state.rate;
      const delta = target - video.currentTime;

      const now = performance.now();
      if (now - lastPublished.current > 250) {
        lastPublished.current = now;
        setDrift(delta);
      }

      if (state.playing) {
        if (video.paused) void video.play().catch(() => undefined);
        if (Math.abs(delta) > HARD_SEEK) {
          video.currentTime = target;
          video.playbackRate = state.rate;
        } else if (Math.abs(delta) > DEAD_ZONE) {
          // Gently run fast/slow to absorb the difference instead of jumping.
          video.playbackRate = state.rate * (delta > 0 ? 1.06 : 0.94);
        } else {
          video.playbackRate = state.rate;
        }
      } else {
        if (!video.paused) video.pause();
        if (Math.abs(delta) > 0.002) video.currentTime = target;
        video.playbackRate = state.rate;
      }

      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [videoRef, active]);

  return drift;
}
